import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, friendlyError } from "@/lib/api";

const TOGGLES = [
  ["address_public", "Show exact address publicly", "Off = revealed only after RSVP"],
  ["guest_list_visible", "Show 'Who's Coming' to guests", ""],
  ["dish_list_visible", "Show the potluck table to guests", ""],
  ["dishes_enabled", "Enable dish contributions", ""],
  ["dietary_enabled", "Ask dietary questions", ""],
  ["allow_additional_guests", "Allow additional guests", ""],
  ["guest_names_required", "Require guest names", ""],
  ["sms_reminders_enabled", "Enable SMS reminders", ""],
  ["sharing_enabled", "Enable guest sharing", ""],
  ["waitlist_enabled", "Enable waitlist when full", ""],
  ["deadline_enforced", "Enforce RSVP deadline", "Closes new RSVPs after the deadline"],
  ["capacity_public", "Show capacity publicly", ""],
];

const TEXT = [
  ["event_name", "Event title", "text"],
  ["hosts", "Hosted by", "text"],
  ["event_date", "Date", "date"],
  ["event_time", "Time (display)", "text"],
  ["address", "Address", "text"],
  ["rsvp_deadline", "RSVP deadline", "date"],
  ["max_capacity", "Maximum capacity", "number"],
  ["max_party_size", "Maximum party size", "number"],
];

export function SettingsTab() {
  const qc = useQueryClient();
  const [s, setS] = useState(null);
  const { data } = useQuery({ queryKey: ["host-settings"], queryFn: async () => (await api.get("/host/settings")).data });
  useEffect(() => { if (data) setS(data); }, [data]);

  if (!s) return <div className="h-40 grid place-items-center"><div className="w-8 h-8 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put("/host/settings", s);
      toast.success("Settings saved.");
      qc.invalidateQueries({ queryKey: ["event"] });
      qc.invalidateQueries({ queryKey: ["host-settings"] });
    } catch (e) { toast.error(friendlyError(e)); }
  };
  const setGtk = (i, field, v) => { const g = [...(s.good_to_know || [])]; g[i] = { ...g[i], [field]: v }; set("good_to_know", g); };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-espresso">Event Settings</h1><p className="text-inkSoft mt-1">Control every detail of your Friendsgiving.</p></div>
        <button onClick={save} className="gold-gradient text-espressoDeep font-bold px-6 py-3 rounded-full shadow-gold active:scale-95 transition" data-testid="settings-save">Save Changes</button>
      </div>

      <div className="rounded-3xl bg-white border border-hair shadow-soft p-6 grid sm:grid-cols-2 gap-4">
        {TEXT.map(([k, label, type]) => (
          <div key={k}>
            <label className="text-eyebrow text-inkMuted">{label}</label>
            <input type={type} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`setting-${k}`}
              className="w-full mt-1.5 bg-cream2/50 border border-hair rounded-xl px-4 py-3 text-espresso focus:border-gold outline-none" />
          </div>
        ))}
      </div>

      <div className="rounded-3xl bg-white border border-hair shadow-soft divide-y divide-hair">
        {TOGGLES.map(([k, label, hint]) => (
          <div key={k} className="flex items-center justify-between px-6 py-4">
            <div><p className="text-espresso font-medium">{label}</p>{hint && <p className="text-sm text-inkMuted">{hint}</p>}</div>
            <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
          </div>
        ))}
      </div>

      <div className="rounded-3xl bg-white border border-hair shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-espresso">Good to Know</h2>
          <button onClick={() => set("good_to_know", [...(s.good_to_know || []), { label: "", value: "" }])} className="flex items-center gap-1.5 text-sm font-semibold text-gold" data-testid="gtk-add"><Plus size={16} /> Add</button>
        </div>
        <div className="space-y-3">
          {(s.good_to_know || []).map((g, i) => (
            <div key={i} className="flex gap-2" data-testid={`gtk-row-${i}`}>
              <input value={g.label} onChange={(e) => setGtk(i, "label", e.target.value)} placeholder="Label" className="w-40 bg-cream2/50 border border-hair rounded-xl px-3 py-2.5 focus:border-gold outline-none" />
              <input value={g.value} onChange={(e) => setGtk(i, "value", e.target.value)} placeholder="Details" className="flex-1 bg-cream2/50 border border-hair rounded-xl px-3 py-2.5 focus:border-gold outline-none" />
              <button onClick={() => set("good_to_know", s.good_to_know.filter((_, x) => x !== i))} className="p-2.5 rounded-lg text-[#D97706] hover:bg-[#D97706]/10" data-testid={`gtk-del-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
